'use client'

import React, { useCallback, useEffect, useState } from 'react'
import Cookies, { CookieGetOptions, CookieSetOptions } from 'universal-cookie'
import { buildCookieTree } from '@dustin-web/microsite-config/src/cookie-state'

const isBrowser = typeof window !== 'undefined'
const isSecure = typeof window !== 'undefined' ? window.location.protocol === 'https:' : false
const cookieJar = isBrowser ? new Cookies() : null
const ONE_YEAR = 60 * 60 * 24 * 365

type DustinCookieSetOptions = CookieSetOptions & {
  expireInOneYear?: boolean
}
export const CookiesContext = React.createContext<{ [key: string]: string } | null>(null)

export const useCookieState = () => {
  const initialRawCookies = React.useContext(CookiesContext)
  const [cookieHeader, setCookieHeader] = useState(buildCookieHeader(initialRawCookies || {}))

  if (!initialRawCookies) {
    throw new Error('useCookies must be used within a Cookies Context')
  }

  const [cookieTree, setCookieTree] = useState(buildCookieTree(initialRawCookies))
  // const [rawCookies, setRawCookies] = useState(initialRawCookies)

  const setCookie = useCallback(
    (name: string, value: string | boolean, opts: DustinCookieSetOptions = {}) => {
      cookieJar?.set(name, value, {
        path: '/',
        maxAge: opts.expireInOneYear ? ONE_YEAR : opts.maxAge,
        secure: isSecure,
        ...opts,
      })
    },
    []
  )

  const removeCookie = (cookieName: string, opts: CookieSetOptions = {}) => {
    cookieJar?.remove(cookieName, { path: '/', ...opts })
  }

  const getRawCookie = <T = string,>(
    cookieName: string,
    opts: CookieGetOptions = {}
  ): T | undefined => {
    return cookieJar?.get(cookieName, opts)
  }

  useEffect(() => {
    if (cookieJar) {
      const cookieListener = () => {
        const newCookies = cookieJar.getAll()
        // setRawCookies(newCookies)
        setCookieTree(buildCookieTree(newCookies))

        const cookieHeader = buildCookieHeader(newCookies || {})
        setCookieHeader(cookieHeader)
      }

      cookieJar?.addChangeListener(cookieListener)

      return () => cookieJar?.removeChangeListener(cookieListener)
    }
  })

  return {
    cookies: cookieTree,
    cookieHeader,
    getRawCookie,
    setCookie,
    removeCookie,
  }
}

export const buildCookieHeader = (cookies: { [key: string]: string }) =>
  Object.entries(cookies || {}).reduce(
    (acc, [key, val]) => acc + `${key}=${encodeURIComponent(val)};`,
    ''
  )
